<template>
  <v-container fluid>
    <breadcrumbs />
    <v-row>
      <v-col cols="12" md="12" xl="9">
        <h1>Внешние сервисы для отзывов</h1>
      </v-col>
      <v-col cols="12" md="12" xl="9">
        <filial-badge :name="filial && filial.name" />
      </v-col>
      <v-col cols="12" md="12" xl="9">
        <p class="review-actions__note">
          После оценки заказа на 5 звёзд в приложении клиенту предлагается оставить отзыв в одном из внешних сервисов для того чтоб получить бонусы. Вы можете указать последовательность выдачи клиенту сервисов перетаскивая плитки, изменять ссылки, а также включать/выключать отдельные сервисы. Переход к следующему сервису происходит в случае если клиент оставит в нем отзыв либо проигнорирует 3 раза запрос на отзыв.
        </p>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="12" xl="9">
        <v-card>
          <loader v-if="loading" />
          <template v-else>
            <div class="review-actions__head">
              <span class="review-actions__eyebrow">Очерёдность показа</span>
              <span class="review-actions__hint">Перетащите за ⋮, чтобы поменять порядок</span>
            </div>

            <draggable
              v-model="items"
              class="review-actions__list"
              handle=".review-action-row__grip"
              :disabled="saving"
              ghost-class="review-action-row_ghost"
              @end="renumber"
            >
              <review-action-row
                v-for="(item, index) in items"
                :key="item.id"
                :value="item"
                :index="index"
                :disabled="saving"
                @input="replace(index, $event)"
              />
            </draggable>

            <div class="review-actions__foot">
              <span v-if="hasChanges">Есть несохранённые изменения</span>
            </div>

            <v-card-text>
              <v-row>
                <v-col cols="4">
                  <v-btn
                    block
                    large
                    color="primary"
                    :loading="saving"
                    :disabled="!hasChanges"
                    @click.prevent="save(true)"
                  >
                    Сохранить
                  </v-btn>
                </v-col>
                <v-col cols="4">
                  <v-btn
                    block
                    large
                    color="success"
                    :loading="saving"
                    :disabled="!hasChanges"
                    @click.prevent="save(false)"
                  >
                    Применить
                  </v-btn>
                </v-col>
                <v-col cols="4">
                  <v-btn
                    block
                    large
                    color="error"
                    :disabled="saving"
                    @click.prevent="goToSettings"
                  >
                    Отменить
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import draggable from 'vuedraggable'
  import Loader from '@/components/loader/Loader.vue'
  import FilialBadge from '@/components/ui/FilialBadge.vue'
  import ReviewActionRow from '@/components/settings/review-actions/ReviewActionRow.vue'
  import snackbarMessage from '@/mixins/snackbar-message'
  import RouteName from '@/models/enums/RouteName'
  import reviewActionsApi from '@/api/reviewActions'

  export default {
    name: 'ReviewActionsSettings',
    components: { draggable, Loader, FilialBadge, ReviewActionRow },
    mixins: [snackbarMessage],
    data () {
      return {
        items: [],
        initial: '',
        loading: false,
        saving: false,
      }
    },
    computed: {
      ...mapGetters({
        filialId: 'branches/getGlobalFilialId',
        filial: 'branches/getGlobalFilial',
      }),
      hasChanges () {
        return JSON.stringify(this.payload()) !== this.initial
      },
    },
    watch: {
      filialId () {
        this.load()
      },
    },
    created () {
      this.load()
    },
    methods: {
      // То, что уходит на сервер и по чему считаются изменения
      payload () {
        return this.items.map((item, index) => ({
          id: item.id,
          isEnabled: item.isEnabled,
          sort: index,
          ...this.linksOf(item),
        }))
      },
      // links: своя ссылка или null (по умолчанию); disabledLinks: ключи со значением '' (не показывать).
      // Пустую строку в links слать нельзя - бек превращает её в null.
      linksOf (item) {
        const own = item.type === 'service'
          ? item.services.map((s) => [s.type, s.link])
          : item.type === 'link' ? [['link', item.link]] : []

        return {
          links: own.reduce((acc, [key, link]) => ({ ...acc, [key]: link || null }), {}),
          disabledLinks: own.filter(([, link]) => link === '').map(([key]) => key),
        }
      },
      apply (list) {
        this.items = list.map((item) => ({ ...item }))
        this.initial = JSON.stringify(this.payload())
      },
      async load () {
        if (!this.filialId) {
          this.showErrorMessage('Не выбран филиал')
          return
        }

        this.loading = true
        try {
          this.apply(await reviewActionsApi.getList(this.filialId))
        } catch (e) {
          this.showErrorFromResponse(e)
        }
        this.loading = false
      },
      replace (index, item) {
        this.items.splice(index, 1, item)
      },
      renumber () {
        this.items = this.items.map((item, index) => ({ ...item, sort: index }))
      },
      async save (redirect) {
        this.saving = true
        try {
          this.apply(await reviewActionsApi.update(this.filialId, this.payload()))
          this.showSuccessMessage('Настройки сохранены')
          if (redirect) this.goToSettings()
        } catch (e) {
          this.showErrorFromResponse(e)
        }
        this.saving = false
      },
      goToSettings () {
        this.$router.push({ name: RouteName.Settings })
      },
    },
  }
</script>

<style scoped lang="less">
.review-actions {
  &__note {
    //max-width: 68ch;
    margin: 0;
    font-size: 13px;
    color: #6d6d6d;
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 14px 18px 0;
  }

  &__eyebrow {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #9a9ea6;
  }

  &__hint { font-size: 12px; color: #9a9ea6; }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 10px 6px;
  }

  &__foot {
    min-height: 20px;
    padding: 0 18px;
    font-size: 12px;
    color: #9a9ea6;
  }
}
</style>
